'use client';

import React, { useState } from 'react';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useRouter } from 'next/navigation';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FastForward, FileText, Info } from 'lucide-react';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { headerTranslations } from '@/doi-tac-module/constants/header-translations';
import { useNavigation } from '@/doi-tac-module/contexts/NavigationContext';

interface PostJobChoiceDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function PostJobChoiceDialog({ open, onOpenChange }: PostJobChoiceDialogProps) {
    const router = useRouter();
    const { language } = useLanguage();
    const { setNavigatingPath } = useNavigation();
    const ht = headerTranslations;

    const { role, user } = useAuth();
    const [showCompanyWarning, setShowCompanyWarning] = useState(false);
    const [pendingPath, setPendingPath] = useState<string | null>(null);

    const handleChoice = (path: string) => {
        /* DANGDON001: Yêu cầu Tên pháp nhân trước khi đăng việc làm */
        if (role === 'company' && !user?.agencyProfile?.companyName) {
            setPendingPath(path);
            setShowCompanyWarning(true);
            return;
        }

        onOpenChange(false);
        setNavigatingPath(path);
        router.push(path);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-headline text-2xl text-center">
                        {ht.postJobDialog?.title[language] || 'Đăng thông tin'}
                    </DialogTitle>
                    <DialogDescription className="text-center">
                        {ht.postJobDialog?.desc[language] || 'Chọn phương thức đăng bài'}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-4">
                    <button
                        onClick={() => handleChoice('/dang-viec-lam-nhanh')}
                        className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all duration-200 cursor-pointer group"
                    >
                        <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                            <FastForward className="h-7 w-7 text-primary" />
                        </div>
                        <div className="text-center">
                            <p className="font-bold text-base">
                                {ht.postJobDialog?.quickPost[language] || 'Đăng nhanh'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                {ht.postJobDialog?.quickPostDesc[language] || 'Dễ dàng, nhanh chóng'}
                            </p>
                        </div>
                    </button>
                    <button
                        onClick={() => handleChoice('/dang-viec-lam-chi-tiet')}
                        className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl border-2 border-border hover:border-[#F2B92A] hover:bg-[#F2B92A]/5 transition-all duration-200 cursor-pointer group"
                    >
                        <div className="h-14 w-14 rounded-full bg-[#F2B92A]/10 flex items-center justify-center group-hover:bg-[#F2B92A]/20 transition-colors">
                            <FileText className="h-7 w-7 text-[#F2B92A]" />
                        </div>
                        <div className="text-center">
                            <p className="font-bold text-base">
                                {ht.postJobDialog?.detailedPost[language] || 'Đăng chi tiết'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                {ht.postJobDialog?.detailedPostDesc[language] || 'Đầy đủ thông tin nội dung công việc'}
                            </p>
                        </div>
                    </button>
                </div>
            </DialogContent>

            {/* DANGDON001: Dialog cảnh báo thiếu Tên pháp nhân */}
            <AlertDialog open={showCompanyWarning} onOpenChange={setShowCompanyWarning}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-slate-900">
                            <Info className="w-5 h-5 text-[#19A6DF]" />
                            Yêu cầu bổ sung thông tin
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-600 leading-relaxed">
                            Để bắt đầu đăng tin tuyển dụng, bạn vui lòng hoàn thiện <strong>Tên pháp nhân công ty</strong>. Việc này giúp định danh doanh nghiệp và tăng mức độ tin cậy đối với các ứng viên tiềm năng.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowCompanyWarning(false)}>Để sau</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-[#19A6DF] hover:bg-[#158bbd] text-white border-none"
                            onClick={() => {
                                setShowCompanyWarning(false);
                                onOpenChange(false); // Close parent dialog too
                                if (pendingPath) {
                                    const url = `/cap-nhat-ho-so?postJobMode=true&redirectUrl=${encodeURIComponent(pendingPath)}`;
                                    router.push(url);
                                }
                            }}
                        >
                            Đi đến cập nhật
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

        </Dialog>
    );
}
